import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Plus, Trash2, Save, X, Loader } from 'lucide-react'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import {
  saveExaminationTemplate,
  getExaminationTemplates,
  deleteExaminationTemplate,
} from '../services/examinationTemplates'
import Button from './Button'
import Input from './Input'
import Card from './Card'

const QuestionnaireTemplateManager = ({ customQuestions, onLoadTemplate, onSaveTemplate }) => {
  const organizationId = useOrganizationId()
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (organizationId) {
      loadTemplates()
    }
  }, [organizationId])

  const loadTemplates = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const result = await getExaminationTemplates(organizationId, 'questionnaire')
      if (result.success) {
        setTemplates(result.data || [])
      }
    } catch (error) {
      console.error('Error loading templates:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name')
      return
    }

    if (!customQuestions || customQuestions.length === 0) {
      alert('No questions to save')
      return
    }

    try {
      setSaving(true)
      await saveExaminationTemplate(organizationId, {
        name: templateName.trim(),
        type: 'questionnaire',
        templateData: customQuestions,
        description: templateDescription.trim() || null,
      })
      setTemplateName('')
      setTemplateDescription('')
      setShowSaveDialog(false)
      await loadTemplates()
      if (onSaveTemplate) {
        onSaveTemplate()
      }
    } catch (error) {
      console.error('Error saving template:', error)
      alert('Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  const handleLoadTemplate = async (template) => {
    if (onLoadTemplate && template.custom_questions_template) {
      onLoadTemplate(template.custom_questions_template)
    }
  }

  const handleDeleteTemplate = async (templateId, e) => {
    e.stopPropagation()
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return
    }

    try {
      await deleteExaminationTemplate(templateId)
      await loadTemplates()
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Questionnaire Templates</h3>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSaveDialog(true)}
            className="flex items-center gap-2"
          >
            <Save size={16} />
            Save Template
          </Button>
        </div>
      </div>

      {/* Save Dialog */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowSaveDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white">Save Template</h4>
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <Input
                  label="Template Name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g., Initial Consultation Questions"
                  required
                />
                <Input
                  label="Description (Optional)"
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Brief description of this template"
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="secondary" onClick={() => setShowSaveDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveTemplate} disabled={saving || !templateName.trim()}>
                    {saving ? (
                      <>
                        <Loader size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Templates List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading templates...</div>
      ) : templates.length === 0 ? (
        <Card variant="soft" className="p-6 text-center">
          <FileText size={48} className="mx-auto mb-4 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400">No templates saved yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Save your current questions as a template for future use
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => (
            <Card
              key={template.id}
              variant="soft"
              hover
              className="p-4 cursor-pointer"
              onClick={() => handleLoadTemplate(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{template.name}</h4>
                  {template.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{template.description}</p>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {template.custom_questions_template?.length || 0} questions
                  </p>
                </div>
                <button
                  onClick={(e) => handleDeleteTemplate(template.id, e)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Delete template"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default QuestionnaireTemplateManager

